"use client"

import { useState, useEffect, useContext } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useFloatingChat } from "../../Contexts/FloatingChatContext"
import { useBookmarkContext } from "../../Contexts/BookmarkProvider"
import { LoginContext } from "../../Contexts/LoginContext"
import axios from "axios"
import { Star, Building2, Globe, Users, Calendar, Package, Warehouse, Award, ShoppingBag } from "lucide-react"
import { FaLanguage, FaBookmark } from "react-icons/fa"
import ScrollableRow from "../../Components/utils/ScrollableRow"
import ProductCard from "../../Components/buyer/ProductCard"
import ReviewHistoryContainer from "../../Components/buyer/ReviewHistoryContainer"
import Loader from "../../Components/utils/Loader"
import { transformProduct } from "../../data/constants"
import { formatCityRegion } from "../../Components/utils/formatAddress"
import SEO from '../../Components/utils/SEO';
import { truncateText } from '../../utils/seoUtils';
import { SEO_SITE_URL } from '../../config/seoConfig';

const VendorDetail = () => {
  const { sellerId } = useParams()
  const navigate = useNavigate()
  const [seller, setSeller] = useState(null)
  const [b2bProfile, setB2bProfile] = useState(null)
  const [products, setProducts] = useState([])
  const [b2bListings, setB2bListings] = useState([])
  const [isLoadingSeller, setIsLoadingSeller] = useState(true)
  const [isLoadingProducts, setIsLoadingProducts] = useState(true)
  const [isLoadingB2BListings, setIsLoadingB2BListings] = useState(false)
  const { openFloatingChat } = useFloatingChat()
  const { addBookmark, removeBookmark, isBookmarked, actionLoading } = useBookmarkContext()
  const { isLogin } = useContext(LoginContext)

  useEffect(() => {
    const fetchSeller = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/${sellerId}`)
        if (response.data.success) {
          setSeller(response.data.seller)
          setB2bProfile(response.data.b2bProfile || null)
        }
      } catch (error) {
        console.error("Error fetching seller:", error)
      } finally {
        setIsLoadingSeller(false)
      }
    }

    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/${sellerId}`,
        )
        if (response.data.success) {
          const transformedProducts = response.data.products?.map(transformProduct)
          setProducts(transformedProducts)
        }
      } catch (error) {
        console.error("Error fetching products:", error)
      } finally {
        setIsLoadingProducts(false)
      }
    }

    fetchSeller()
    fetchProducts()
  }, [sellerId])

  useEffect(() => {
    const fetchB2BListings = async () => {
      if (!b2bProfile?._id) {
        setB2bListings([])
        return
      }
      setIsLoadingB2BListings(true)
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/seller/${b2bProfile._id}/listings`,
        )
        if (response.data.success) {
          setB2bListings(response.data.listings || [])
        }
      } catch (error) {
        console.error("Error fetching B2B listings:", error)
        setB2bListings([])
      } finally {
        setIsLoadingB2BListings(false)
      }
    }

    fetchB2BListings()
  }, [b2bProfile?._id])

  // Map reviews to match ReviewHistoryContainer's expected format
  const mappedReviews =
    seller?.reviews.map((review) => ({
      id: review._id,
      image: seller.profileImage,
      title: b2bProfile?.companyName || seller.displayName,
      sellerName: b2bProfile?.companyName || seller.displayName,
      reviewerName: review.userId.username,
      overallRating: review.overallRating,
      ratings: review.ratings, // Already an array of numbers matching the questions
      comment: review.comment,
      images: review.images || [],
      tipPaid: false, // Not in schema, default to false
      tipAmount: 0, // Not in schema, default to 0
    })) || []

  const canonicalPath = `/vendorDetail/${sellerId}`

  if (isLoadingSeller) {
    return (
      <>
        <SEO title="Seller" description="Loading seller profile" canonicalPath={canonicalPath} noIndex />
        <Loader type="simpleMax" />
      </>
    )
  }

  const isBusinessSeller = Boolean(b2bProfile)
  const profileName = b2bProfile?.companyName || seller.displayName
  const buyerAddr = seller?.userId?.buyerAddress
  const profileLocation = formatCityRegion(b2bProfile?.businessAddress) || formatCityRegion(seller.sellerAddress) || formatCityRegion(buyerAddr)
  const profileAbout = seller.description
  const seoDescription = profileAbout
    ? truncateText(profileAbout, 160)
    : `View ${profileName} on Chocowi and explore seller listings.`
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: profileName,
    description: seoDescription,
    url: `${SEO_SITE_URL}${canonicalPath}`,
    image: seller.profileImage || undefined
  }

  return (
    <div className="vendor-container">
      <SEO
        title={profileName}
        description={seoDescription}
        canonicalPath={canonicalPath}
        image={seller.profileImage}
        schema={schema}
      />
      <main className='mainSection'>
        {/* Top Section - Vendor Profile */}
        <div className={`vendor-profile ${isBusinessSeller ? "vendor-profile-business" : ""}`}>
          <div className="profile-image-container">
            <img src={seller.profileImage || "/placeholder.svg"} alt={profileName} className="profile-image" />
          </div>
          <div className="profile-details">
            <h2 className="profile-name">{profileName}</h2>
            <p className="profile-location">{profileLocation}</p>
            {isBusinessSeller && (
              <div className="vendor-business-badges">
                <span className="vendor-business-badge">
                  <Building2 size={12} /> {b2bProfile?.natureOfOrg || "Business Seller"}
                </span>
                {b2bProfile?.numberOfEmployees && (
                  <span className="vendor-business-badge neutral">
                    <Users size={12} /> {b2bProfile.numberOfEmployees} Employees
                  </span>
                )}
              </div>
            )}
            {isLogin && (
              <button
                type="button"
                className={`vendorBookmarkBtn ${isBookmarked(seller._id) ? 'bookmarked' : ''}`}
                onClick={() => isBookmarked(seller._id) ? removeBookmark(seller._id) : addBookmark(seller)}
                disabled={actionLoading[seller._id]}
                title={isBookmarked(seller._id) ? 'Remove from bookmarks' : 'Add to bookmarks'}
              >
                {actionLoading[seller._id] ? <Loader type="miniPlus" /> : <FaBookmark />}
                {isBookmarked(seller._id) ? ' Bookmarked' : ' Bookmark'}
              </button>
            )}
          </div>
        </div>

        {/* Middle Section - Info Card */}
        <div className="vendor-info-card">
          <div className="info-card-content">
            {/* Left Side */}
            <div className="info-left-section">
              {/* Languages */}
              <div className="info-block">
                <div className="info-icon language-icon">
                  <FaLanguage />
                </div>
                <div className="info-content">
                  <h3>Languages</h3>
                  <p>{seller.userId.languages}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="info-block">
                <div className="info-icon rating-icon">
                  <Star className="star-icon" fill="currentColor" />
                </div>
                <div className="info-content">
                  <h3>Rating</h3>
                  <div className="rating-container">
                    <span className="rating-value">{seller.rating.toFixed(1)}</span>
                    <span className="rating-count">({seller.noOfReviews})</span>
                  </div>
                </div>
              </div>

              <div className="info-block">
                <div className="info-icon rating-icon">
                  <ShoppingBag className="star-icon" />
                </div>
                <div className="info-content">
                  <h3>Orders Completed</h3>
                  <p>{seller.productsSold || 0}</p>
                </div>
              </div>

              {/* Contact Button */}
              <div className="contact-button-container">
                <button
                  className="contact-button"
                  onClick={() => {
                    if (!isLogin) {
                      navigate('/signin', { state: { returnTo: `/vendorDetail/${sellerId}` } });
                      return;
                    }
                    openFloatingChat(seller.userId._id);
                  }}
                >
                  {isBusinessSeller ? "Contact Supplier" : "Contact Seller"}
                </button>
              </div>
            </div>

            {/* Right Side - About */}
            <div className="info-right-section">
              <h3>About</h3>
              <p>{profileAbout}</p>

              {b2bProfile && (
                <div className="vendorBusinessDetails">
                  <h3>Business Profile</h3>
                  <div className="vendorBusinessGrid">
                    <div className="vendorBusinessItem">
                      <span><Building2 size={14} /> Company</span>
                      <strong>{b2bProfile.companyName || 'N/A'}</strong>
                    </div>
                    <div className="vendorBusinessItem">
                      <span><Building2 size={14} /> Organization</span>
                      <strong>{b2bProfile.natureOfOrg || 'N/A'}</strong>
                    </div>
                    <div className="vendorBusinessItem">
                      <span><Users size={14} /> Employees</span>
                      <strong>{b2bProfile.numberOfEmployees || 'N/A'}</strong>
                    </div>
                    <div className="vendorBusinessItem">
                      <span><Calendar size={14} /> Founded</span>
                      <strong>{b2bProfile.yearFounded || 'N/A'}</strong>
                    </div>
                    <div className="vendorBusinessItem">
                      <span><ShoppingBag size={14} /> Orders Completed</span>
                      <strong>{seller.productsSold || 0}</strong>
                    </div>
                    <div className="vendorBusinessItem">
                      <span><Globe size={14} /> Location</span>
                      <strong>{profileLocation || '-'}</strong>
                    </div>
                    <div className="vendorBusinessItem">
                      <span><Package size={14} /> Production Capacity</span>
                      <strong>{b2bProfile.productionCapacity || 'N/A'}</strong>
                    </div>
                    <div className="vendorBusinessItem">
                      <span><Warehouse size={14} /> Inventory Capacity</span>
                      <strong>{b2bProfile.inventoryCapacity || 'N/A'}</strong>
                    </div>
                    <div className="vendorBusinessItem fullWidth">
                      <span><Award size={14} /> Certifications</span>
                      <strong>{(b2bProfile.certifications || []).map((item) => item?.name).filter(Boolean).join(', ') || 'N/A'}</strong>
                    </div>
                    <div className="vendorBusinessItem fullWidth">
                      <span><Globe size={14} /> Export Markets</span>
                      <strong>{(b2bProfile.exportMarkets || []).join(', ') || 'N/A'}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - Postings */}
        <div className="vendor-postings">
          <h2>Postings</h2>
          <ScrollableRow
            items={products}
            itemsPerRow={3}
            renderItem={(product) => <ProductCard key={product._id} product={product} />}
            isLoading={isLoadingProducts}
            noItemsMessage="No products available."
          />
        </div>

        {isBusinessSeller && (
          <div className="vendor-postings">
            <h2>B2B Listings</h2>
            <ScrollableRow
              items={b2bListings}
              itemsPerRow={3}
              renderItem={(listing) => <ProductCard key={listing._id} b2bListing={listing} />}
              isLoading={isLoadingB2BListings}
              noItemsMessage="No B2B listings available."
            />
          </div>
        )}

        {/* Reviews Section */}
        <div className="vendor-reviews">
          <h2>Reviews</h2>
          {mappedReviews.length > 0 ? (
            <ReviewHistoryContainer reviews={mappedReviews} isMyReview={false} isProductReview={true} />
          ) : (
            <div className="no-reviews">No reviews available</div>
          )}
        </div>
      </main>
    </div>
  )
}

export default VendorDetail